import { ListChecks, Check } from 'lucide-react';

import type { ProjectPlanSnapshotProps } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { ChatCard, mix } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { SkBar, SkCircle, SkChip } from './skeletons';

type PhaseState = 'done' | 'active' | 'upcoming';

const DEEP_PURPLE = 'var(--color-purple-700)';
const PURPLE = 'var(--color-purple-400)';
const GREEN = 'var(--color-green-500)';
const GREY = 'var(--color-gray-400)';

function accentFor(state: PhaseState): string {
  if (state === 'done') return GREEN;
  if (state === 'active') return DEEP_PURPLE;
  return GREY;
}

function statusLabel(state: PhaseState): string {
  if (state === 'done') return 'Complete';
  if (state === 'active') return 'In progress';
  return 'Upcoming';
}

export function ProjectPlanSnapshotWidget({ badge, phases }: ProjectPlanSnapshotProps) {
  return (
    <ChatCard className="flex flex-col gap-[13px] p-4 text-sm">
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: mix(DEEP_PURPLE, 14), border: `1px solid ${mix(DEEP_PURPLE, 28)}`, color: PURPLE }}
        >
          <ListChecks className="size-[14px]" aria-hidden />
        </span>
        <span className="text-[15px] font-bold text-foreground">Project plan</span>
        {badge && (
          <span
            className="ml-auto inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] font-mono text-[9.5px] font-bold uppercase tracking-[0.05em]"
            style={{ color: PURPLE, background: mix(DEEP_PURPLE, 13), border: `1px solid ${mix(DEEP_PURPLE, 28)}` }}
          >
            <span className="size-[5px] rounded-full" style={{ background: PURPLE }} aria-hidden />
            {badge}
          </span>
        )}
      </div>

      <div className="flex flex-col">
        {phases.map((phase, i) => {
          const last = i === phases.length - 1;
          const isDone = phase.state === 'done';
          const isActive = phase.state === 'active';
          const accent = accentFor(phase.state);
          return (
            <div key={phase.number} className="grid grid-cols-[26px_1fr] items-start gap-3">
              {/* node + connector */}
              <div className="flex flex-col items-center self-stretch">
                <span
                  className="relative inline-flex size-[26px] shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: isDone
                      ? 'linear-gradient(135deg, var(--color-green-600), var(--color-green-500))'
                      : isActive
                        ? 'var(--gradient-primary)'
                        : 'var(--secondary)',
                    border: !isDone && !isActive ? '1px dashed var(--border-strong)' : 'none',
                    boxShadow: isActive ? '0 0 0 3px rgba(124,58,237,0.18)' : 'none',
                  }}
                >
                  {isDone && <Check className="size-[13px] text-white" strokeWidth={2.6} aria-hidden />}
                  {isActive && <span className="size-[7px] rounded-full bg-white" aria-hidden />}
                  {!isDone && !isActive && (
                    <span className="text-[10px] font-bold text-muted-foreground">{phase.number}</span>
                  )}
                </span>
                {!last && (
                  <span
                    className="my-[3px] min-h-[14px] w-0.5 flex-1 rounded-[2px]"
                    style={{ background: isDone ? GREEN : 'var(--border)' }}
                    aria-hidden
                  />
                )}
              </div>

              {/* label + note */}
              <div className={cn('flex min-w-0 flex-col gap-1', last ? 'pb-0' : 'pb-3.5')}>
                <div className="flex flex-wrap items-baseline gap-2">
                  <span
                    className={cn(
                      'text-[13px] font-bold',
                      isActive || isDone ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {capitalizeFirst(phase.name)}
                  </span>
                  <span
                    className="font-mono text-[9px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: accent }}
                  >
                    {statusLabel(phase.state)}
                  </span>
                </div>
                {isActive && phase.note && (
                  <div
                    className="flex items-start gap-2 rounded-[10px] px-[11px] py-[9px]"
                    style={{ background: mix(DEEP_PURPLE, 8), border: `1px solid ${mix(DEEP_PURPLE, 22)}` }}
                  >
                    <span
                      className="inline-flex size-[18px] shrink-0 items-center justify-center rounded-full text-[8px] font-extrabold text-white"
                      style={{ background: 'var(--gradient-primary)' }}
                      aria-hidden
                    >
                      A
                    </span>
                    <span className="text-[11.5px] leading-[1.45] text-foreground text-pretty">
                      {capitalizeFirst(phase.note)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ChatCard>
  );
}

export function ProjectPlanSnapshotSkeleton({ className, rows = 5 }: { className?: string; rows?: number }) {
  return (
    <ChatCard className={cn('flex flex-col gap-[13px] p-4', className)}>
      <div className="flex items-center gap-2.5">
        <SkCircle size="28px" radius="8px" />
        <SkBar width="120px" height="13px" />
        <span className="ml-auto">
          <SkChip width="56px" />
        </span>
      </div>
      <div className="flex flex-col">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="grid grid-cols-[26px_1fr] items-start gap-3">
            <div className="flex flex-col items-center self-stretch">
              <SkCircle size="26px" />
              {i < rows - 1 && <span className="my-[3px] min-h-[16px] w-0.5 flex-1 rounded-full bg-muted" />}
            </div>
            <div className={i < rows - 1 ? 'pb-3.5' : ''}>
              <SkBar width={`${52 - i * 4}%`} height="12px" />
            </div>
          </div>
        ))}
      </div>
    </ChatCard>
  );
}
